<template>
  <div>
    <b-card>
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <div class="mb-0 d-flex align-items-center" style="gap: 8px;">
            <h5 class="mb-0">Claims on this order</h5>
            <span v-if="summary.open > 0" class="claimBadge redBadge">{{ summary.open }} open</span>
          </div>
          <router-link :to="{ name: 'OrderClaimNew' }" class="btn btn-success btn-sm"><i class="fas fa-plus mr-1"></i> New Claim</router-link>
        </div>
      </template>

      <LoadingIndicator v-if="loading" centered />
      <div v-else-if="claims.length">
        <base-accordion
          v-for="claim in claims"
          :key="claim.id"
          class="mb-3 claim-accordion"
          :class="{ 'claim-highlight': highlightClaimId === claim.id }"
          :data-claim-id="claim.id"
          :is-open="openClaimId === claim.id"
          @toggle="openClaimId = openClaimId === claim.id ? null : claim.id"
        >
          <template #header>
            <div class="d-flex justify-content-between align-items-start w-100 mr-3">
              <div>
                <div class="d-flex align-items-center" style="gap: 6px;">
                  <strong class="blue-text">CLM-{{ String(claim.id).padStart(4, '0') }}</strong>
                  <span :class="['claimBadge', statusBadgeClass(claim.status)]">{{ statusLabel(claim.status) }}</span>
                  <span :class="['claimBadge', severityBadgeClass(claim.severity)]">{{ capitalize(claim.severity) }}</span>
                </div>
                <div class="text-muted mt-1 d-flex align-items-center" style="font-size: 0.8rem; gap: 20px;">
                  <span>{{ categoryLabel(claim.category) }}<template v-if="claim.subcategory"> &rsaquo; {{ subcategoryLabel(claim.subcategory) }}</template></span>
                  <span v-if="claim.vehicle">{{ claim.vehicle.year }} {{ claim.vehicle.make }} {{ claim.vehicle.model }}</span>
                  <b-link v-if="claim.carrier && claim.carrier.contact" :to="{ name: 'CarrierDetails', params: { id: claim.carrier.id } }">{{ claim.carrier.contact.company }}</b-link>
                  <span v-if="claim.createdAt">Opened {{ formatDate(claim.createdAt) }}</span>
                  <span v-if="claim.closedAt !== null">Closed {{ formatDate(claim.closedAt) }}</span>
                </div>
              </div>
              <div class="text-right">
                <div v-if="claim.status == 'closed_paid' || claim.status == 'closed_denied'">
                  <div :class="['font-weight-semibold', claim.amountRecovered > 0 ? 'green-text' : 'gray-text']">Recovered {{ claim.amountRecovered | formatMoney }}</div>
                  <small v-if="resolvedDays(claim) !== null" class="green-text">
                    <i class="fas fa-check mr-1"></i> Resolved in {{ resolvedDays(claim) }} days
                  </small>
                </div>
                <div v-else>
                  <div v-if="claim.amountPaid" class="font-weight-semibold">{{ claim.amountPaid | formatMoney }}</div>
                  <small v-if="getSlaStatus(claim)" :class="getSlaStatus(claim).color">
                    <i :class="`fas ${getSlaStatus(claim).icon} mr-1`"></i> {{ getSlaStatus(claim).text }} 
                  </small>
                </div>
              </div>
            </div>
          </template>
          <!-- Claim Actions -->
          <div class="d-flex justify-content-end" style="gap: 6px;">
            <b-button v-if="claim.status !== 'closed_paid' && claim.status !== 'closed_denied'" size="sm" variant="outline-success" @click="openCloseModal(claim)"><i class="fas fa-check mr-1"></i> Close</b-button>
            <b-button size="sm" variant="outline-danger" @click="deleteClaim(claim)"><i class="far fa-trash-alt"></i></b-button>
          </div>
          <div style="display: flex; flex-direction: column; gap: 28px; margin-top: 16px;">
          <!-- Row 1: Classification + Documentation -->
          <div class="d-flex" style="gap: 28px; align-items: stretch;">
            <div class="card" style="width: 400px; min-width: 0; flex-shrink: 0;">
              <h5 class="card-header d-flex justify-content-between align-items-center">
                Classification
                <template v-if="isClaimOpen(claim)">
                  <div v-if="editingClaimId === claim.id && editingSection === 'classification'" style="display:flex;gap:4px;">
                    <b-button size="sm" variant="success" @click="saveEditClaim(claim.id)"><i class="fas fa-check mr-1"></i>Save</b-button>
                    <b-button size="sm" variant="outline-secondary" @click="cancelEditClaim"><i class="fas fa-times"></i></b-button>
                  </div>
                  <b-button v-else size="sm" variant="outline-primary" @click="startEditClaim(claim, 'classification')"><i class="fas fa-edit"></i></b-button>
                </template>
              </h5>
              <div class="claim-field">
                <template v-if="editingClaimId === claim.id && editingSection === 'classification'">
                  <div class="claim-field-label">Status</div>
                  <b-form-select v-model="editForm.status" :options="statusSelectOptions" size="sm" />
                </template>
                <template v-else>
                  <b>Status:</b> <span :class="statusTextClass(claim.status)">{{ statusLabel(claim.status) }}</span>
                  <div v-if="claim.status === 'escalated' && claim.escalationReason" class="mt-1"><b>Escalation Reason:</b> {{ claim.escalationReason }}</div>
                </template>
              </div>
              <div class="claim-field">
                <template v-if="editingClaimId === claim.id && editingSection === 'classification'">
                  <div class="claim-field-label">Category</div>
                  <b-form-select v-model="editForm.category" :options="categorySelectOptions" size="sm" class="mb-1" @change="editForm.subcategory = null" />
                  <b-form-select v-model="editForm.subcategory" :options="subcategorySelectOptions" size="sm" />
                </template>
                <template v-else>
                  <b>Category:</b> {{ categoryLabel(claim.category) }}<template v-if="claim.subcategory"> &rsaquo; {{ subcategoryLabel(claim.subcategory) }}</template>
                </template>
              </div>
              <div class="claim-field">
                <template v-if="editingClaimId === claim.id && editingSection === 'classification'">
                  <div class="claim-field-label">Severity</div>
                  <b-form-select v-model="editForm.severity" :options="severitySelectOptions" size="sm" />
                </template>
                <template v-else>
                  <b>Severity:</b> <span :class="severityTextClass(claim.severity)">{{ capitalize(claim.severity) }}</span>
                </template>
              </div>
              <div class="claim-field" v-if="claim.vehicle">
                <b>Vehicle:</b> {{ claim.vehicle.year }} {{ claim.vehicle.make }} {{ claim.vehicle.model }}
              </div>
              <div class="claim-field" v-if="claim.assignedUser">
                <b>Assigned Agent:</b> <b-link :to="{ name: 'UserDetails', params: { id: claim.assignedUser.id } }">{{ claim.assignedUser.username }}</b-link>
              </div>
            </div>

            <div class="card" style="flex: 1; min-width: 0;">
              <h5 class="card-header d-flex justify-content-between align-items-center">
                Documentation
                <template v-if="isClaimOpen(claim)">
                  <div v-if="editingClaimId === claim.id && editingSection === 'documentation'" style="display:flex;gap:4px;">
                    <b-button size="sm" variant="success" @click="saveEditClaim(claim.id)"><i class="fas fa-check mr-1"></i>Save</b-button>
                    <b-button size="sm" variant="outline-secondary" @click="cancelEditClaim"><i class="fas fa-times"></i></b-button>
                  </div>
                  <b-button v-else size="sm" variant="outline-primary" @click="startEditClaim(claim, 'documentation')"><i class="fas fa-edit"></i></b-button>
                </template>
              </h5>
              <div class="claim-field" v-if="(editingClaimId === claim.id && editingSection === 'documentation') || claim.photosReceived">
                <template v-if="editingClaimId === claim.id && editingSection === 'documentation'">
                  <div class="claim-field-label">Photos</div>
                  <b-form-select v-model="editForm.photosReceived" :options="photosReceivedOptions" size="sm" />
                </template>
                <template v-else>
                  <b>Photos:</b> <span :class="photosColorClass(claim.photosReceived)">{{ capitalize(claim.photosReceived) }}</span>
                </template>
              </div>
              <div class="claim-field" v-if="(editingClaimId === claim.id && editingSection === 'documentation') || claim.bolDocumented != null">
                <template v-if="editingClaimId === claim.id && editingSection === 'documentation'">
                  <div class="claim-field-label">BOL Documented</div>
                  <b-form-select v-model="editForm.bolDocumented" :options="booleanOptions" size="sm" />
                </template>
                <template v-else>
                  <b>BOL Documented:</b> <span :class="boolColorClass(claim.bolDocumented)">{{ claim.bolDocumented ? 'Yes' : 'No' }}</span>
                </template>
              </div>
              <div class="claim-field" v-if="(editingClaimId === claim.id && editingSection === 'documentation') || claim.slaDays">
                <template v-if="editingClaimId === claim.id && editingSection === 'documentation'">
                  <div class="claim-field-label">SLA Target</div>
                  <b-form-input v-model.number="editForm.slaDays" type="number" size="sm" />
                </template>
                <template v-else><b>SLA Target:</b> {{ claim.slaDays }} days</template>
              </div>
              <div class="claim-field" v-if="(editingClaimId === claim.id && editingSection === 'documentation') || claim.insuranceInvolved != null">
                <template v-if="editingClaimId === claim.id && editingSection === 'documentation'">
                  <div class="claim-field-label">Insurance Involved</div>
                  <b-form-select v-model="editForm.insuranceInvolved" :options="booleanOptions" size="sm" />
                </template>
                <template v-else>
                  <b>Insurance Involved:</b> <span :class="boolColorClass(claim.insuranceInvolved)">{{ claim.insuranceInvolved ? 'Yes' : 'No' }}</span>
                </template>
              </div>
              <div class="claim-field" v-if="(editingClaimId === claim.id && editingSection === 'documentation') || claim.emailThreadUrl">
                <template v-if="editingClaimId === claim.id && editingSection === 'documentation'">
                  <div class="claim-field-label">Email Thread</div>
                  <b-form-input v-model="editForm.emailThreadUrl" size="sm" />
                </template>
                <template v-else>
                  <b><a :href="claim.emailThreadUrl" target="_blank" rel="noopener">Email Thread</a></b>
                </template>
              </div>
              <div class="claim-field" v-if="(editingClaimId === claim.id && editingSection === 'documentation') || claim.description">
                <template v-if="editingClaimId === claim.id && editingSection === 'documentation'">
                  <div class="claim-field-label">Description</div>
                  <b-form-textarea v-model="editForm.description" size="sm" rows="2" />
                </template>
                <template v-else><b>Description:</b> {{ claim.description }}</template>
              </div>
            </div>
          </div>

          <!-- Row 2: Financial & Fault + Notes -->
          <div class="d-flex" style="gap: 28px; align-items: stretch;">
            <div class="card" style="width: 300px; min-width: 0; flex-shrink: 0;">
              <h5 class="card-header d-flex justify-content-between align-items-center">
                Financial &amp; Fault
                <template v-if="isClaimOpen(claim)">
                  <div v-if="editingClaimId === claim.id && editingSection === 'financial'" style="display:flex;gap:4px;">
                    <b-button size="sm" variant="success" @click="saveEditClaim(claim.id)"><i class="fas fa-check mr-1"></i>Save</b-button>
                    <b-button size="sm" variant="outline-secondary" @click="cancelEditClaim"><i class="fas fa-times"></i></b-button>
                  </div>
                  <b-button v-else size="sm" variant="outline-primary" @click="startEditClaim(claim, 'financial')"><i class="fas fa-edit"></i></b-button>
                </template>
              </h5>
              <div class="claim-field" v-if="(editingClaimId === claim.id && editingSection === 'financial') || claim.amountPaid != null">
                <template v-if="editingClaimId === claim.id && editingSection === 'financial'">
                  <div class="claim-field-label">Amount Paid</div>
                  <b-form-input v-model.number="editForm.amountPaid" type="number" size="sm" />
                </template>
                <template v-else><b>Amount Paid:</b> {{ claim.amountPaid | formatMoney }}</template>
              </div>
              <div class="claim-field" v-if="(editingClaimId === claim.id && editingSection === 'financial') || claim.amountToRecover != null">
                <template v-if="editingClaimId === claim.id && editingSection === 'financial'">
                  <div class="claim-field-label">To Recover</div>
                  <b-form-input v-model.number="editForm.amountToRecover" type="number" size="sm" />
                </template>
                <template v-else><b>To Recover:</b> {{ claim.amountToRecover | formatMoney }}</template>
              </div>
              <div class="claim-field" v-if="(editingClaimId === claim.id && editingSection === 'financial') || claim.repairEstimate != null">
                <template v-if="editingClaimId === claim.id && editingSection === 'financial'">
                  <div class="claim-field-label">Repair Estimate</div>
                  <b-form-input v-model.number="editForm.repairEstimate" type="number" size="sm" />
                </template>
                <template v-else><b>Repair Estimate:</b> {{ claim.repairEstimate | formatMoney }}</template>
              </div>
              <div class="claim-field" v-if="(editingClaimId === claim.id && editingSection === 'financial') || claim.faultParty">
                <template v-if="editingClaimId === claim.id && editingSection === 'financial'">
                  <div class="claim-field-label">Fault Party</div>
                  <b-form-select v-model="editForm.faultParty" :options="faultPartyOptions" size="sm" />
                </template>
                <template v-else><b>Fault Party:</b> {{ capitalize(claim.faultParty) }}</template>
              </div>
            </div>

            <div class="card" style="flex: 1; min-width: 0;">
              <h5 class="card-header d-flex justify-content-between align-items-center">
                <span>Notes<span v-if="claimNotes[claim.id] && claimNotes[claim.id].length" class="ml-1 font-weight-normal text-muted small">({{ claimNotes[claim.id].length }})</span></span>
                <b-button v-if="isClaimOpen(claim)" size="sm" variant="outline-secondary" @click="toggleNoteForm(claim.id)"><i class="fas fa-plus mr-1"></i> Add Note</b-button>
              </h5>
              <div v-if="showNoteForm[claim.id]" class="p-3 border-bottom">
                <claim-note-form :saving="!!noteSaving[claim.id]" :initial-status="claim.status" @submit="d => submitInlineNote(claim.id, d)" @cancel="toggleNoteForm(claim.id)" />
              </div>
              <div v-if="claimNotes[claim.id] && claimNotes[claim.id].length">
                <div v-for="note in claimNotes[claim.id]" :key="note.id" class="claim-note-row">
                  <span class="claim-note-date text-muted">{{ formatNoteDate(note.createdAt) }}</span>
                  <span class="claim-note-user">{{ note.user ? note.user.name : '—' }}</span>
                  <div class="claim-note-text">
                    <template v-if="editingNote && editingNote.noteId === note.id">
                      <b-form-textarea v-model="editingNote.note" size="sm" rows="2" @keyup.escape="cancelEditNote" />
                    </template>
                    <template v-else>{{ note.note }}</template>
                  </div>
                  <div class="claim-note-actions">
                    <template v-if="editingNote && editingNote.noteId === note.id">
                      <b-button size="sm" variant="success" @click="saveEditNote(claim.id)" title="Save"><i class="fas fa-check"></i></b-button>
                      <b-button size="sm" variant="outline-secondary" @click="cancelEditNote" title="Cancel"><i class="fas fa-times"></i></b-button>
                    </template>
                    <template v-else-if="isClaimOpen(claim) && (isAdmin || (note.user && note.user.id && note.user.id == currentUserId))">
                      <b-button v-if="isAdmin || canEditNote(note)" size="sm" variant="outline-primary" @click="startEditNote(claim.id, note)" title="Edit"><i class="fas fa-edit"></i></b-button>
                      <b-button size="sm" variant="outline-danger" @click="deleteNote(claim.id, note)" title="Delete"><i class="fas fa-trash"></i></b-button>
                    </template>
                  </div>
                </div>
              </div>
              <div v-else class="px-3 py-2 text-muted small">No notes yet.</div>
            </div>
          </div>

          <!-- Row 3: Attachments -->
          <div v-if="claimAttachments[claim.id]" class="card">
            <h5 class="card-header d-flex justify-content-between align-items-center">
              <span>Attachments<span v-if="claimAttachments[claim.id].length" class="ml-1 font-weight-normal text-muted small">({{ claimAttachments[claim.id].length }})</span></span>
              <b-button v-if="isClaimOpen(claim)" size="sm" variant="outline-secondary" @click="$set(showAttForm, claim.id, !showAttForm[claim.id])">
                <i class="fas fa-plus mr-1"></i> Add Attachment
              </b-button>
            </h5>
            <div v-if="showAttForm[claim.id]" class="p-3 border-bottom">
              <attachment-form :ref="`attForm-${claim.id}`" :saving="!!attSaving[claim.id]" @submit="d => saveClaimAttachment(claim.id, d)" />
            </div>
            <table v-if="claimAttachments[claim.id].length" class="table table-sm mb-0" style="margin: 0 8px; width: calc(100% - 16px);">
              <thead>
                <tr>
                  <th style="width:30px;"></th>
                  <th>Name</th>
                  <th>Filename</th>
                  <th>Size</th>
                  <th>Uploaded by</th>
                  <th>Date</th>
                  <th style="width:88px;"></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="att in claimAttachments[claim.id]">
                  <tr :key="att.id" class="claim-att-row-clickable" @click="toggleAttPreview(claim.id, att)">
                    <td><i :class="expandedAtt === `${claim.id}-${att.id}` ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="text-muted"></i></td>
                    <td><i class="fas fa-paperclip mr-1 text-muted"></i>{{ att.name }}</td>
                    <td>{{ att.filename || '—' }}</td>
                    <td>{{ att.filesize ? formatFileSize(att.filesize) : '—' }}</td>
                    <td>{{ att.user ? att.user.name : '—' }}</td>
                    <td>{{ att.createdAt ? formatDate(att.createdAt) : '—' }}</td>
                    <td @click.stop style="white-space: nowrap;">
                      <b-button size="sm" variant="outline-primary" @click="downloadAttachment(claim.id, att)" title="Download"><i class="fas fa-download"></i></b-button>
                      <b-button v-if="isClaimOpen(claim)" size="sm" variant="outline-danger" @click="deleteAttachment(claim.id, att)" title="Delete"><i class="fas fa-trash"></i></b-button>
                    </td>
                  </tr>
                  <tr v-if="expandedAtt === `${claim.id}-${att.id}`" :key="`prev-${att.id}`">
                    <td colspan="7" class="p-2">
                      <img v-if="isImage(att.filename || att.name)" :src="attPreviewUrls[`${claim.id}-${att.id}`]" class="img-fluid" />
                      <object v-else-if="isPdf(att.filename || att.name)" :data="attPreviewUrls[`${claim.id}-${att.id}`]" type="application/pdf" width="100%" height="500px"></object>
                      <span v-else class="text-muted small">No preview available for this file type.</span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
            <div v-if="!claimAttachments[claim.id].length" class="px-3 py-2 text-muted small">No attachments yet.</div>
          </div>
          </div>
        </base-accordion>

      </div>
      <div v-else class="alert alert-info mb-0">
        No claims have been recorded yet.
      </div>
   
      <!-- Summary -->
        <div class="row text-center mb-3 no-gutters" style="gap: 8px; display: flex;">
          <div class="flex-fill">
            <div class="summary-box p-3 rounded">
              <div class="text-muted-custom">TOTAL EXPOSURE</div>
              <h5>{{ summary.totalExposure | formatMoney }}</h5>
            </div>
          </div>
          <div class="flex-fill">
            <div class="summary-box p-3 rounded">
              <div class="text-muted-custom">OPEN</div>
              <h5 :class="summary.open > 0 ? 'red-text' : ''">{{ summary.open }}</h5>
            </div>
          </div>
          <div class="flex-fill">
            <div class="summary-box p-3 rounded">
              <div class="text-muted-custom">CLOSED</div>
              <h5 :class="summary.closed > 0 ? 'green-text' : ''">{{ summary.closed }}</h5>
            </div>
          </div>
          <div class="flex-fill">
            <div class="summary-box p-3 rounded">
              <div class="text-muted-custom">RECOVERED</div>
              <h5 :class="summary.recovered > 0 ? 'green-text' : ''">{{ summary.recovered | formatMoney }}</h5>
            </div>
          </div>
        </div>
       </b-card>
    <claim-close-modal v-model="closeModal.show" :claim-id="closeModal.claimId" @submit="handleCloseSubmit" />
  </div>
   
</template>

<script>
import { differenceInDays } from 'date-fns';
import { ApiClient } from '@/api/ApiClient';
import BaseAccordion from '@/components/shared/BaseAccordion';
import LoadingIndicator from '@/components/shared/LoadingIndicator.vue';
import ClaimNoteForm from '@/components/shared/form/ClaimNoteForm.vue';
import ClaimCloseModal from '@/components/shared/modals/ClaimCloseModal.vue';
import AttachmentForm from '@/components/shared/AttachmentForm.vue';
import PermissionsMixin from '@/mixins/permissions.js';
import { claimSubcategories, claimCategoryLabels, claimStatusOptions, claimSeverityOptions, claimFaultPartyOptions, claimPhotosOptions, claimBooleanOptions } from '@/helpers/claimOptions';
export default {
  name: 'OrderClaims',
  mixins: [PermissionsMixin],
  components: { BaseAccordion, LoadingIndicator, ClaimNoteForm, ClaimCloseModal, AttachmentForm },
  data() {
    return {
      claims: [],
      claimNotes: {},
      claimAttachments: {},
      editingClaimId: null,
      editingSection: null,
      editForm: {},
      editingNote: null,
      openClaimId: null,
      highlightClaimId: null,
      loading: false,
      now: Date.now(),
      expandedAtt: null,
      attPreviewUrls: {},
      attSaving: {},
      showAttForm: {},
      showNoteForm: {},
      noteSaving: {},
      closeModal: { show: false, claimId: null },
    };
  },
  computed: {
    currentUserId() {
      return this.$store.state.auth.user && this.$store.state.auth.user.id;
    },
    isAdmin() {
      return this.tokenCan('admin');
    },
    statusOptions() {
      return claimStatusOptions;
    },
    statusSelectOptions() {
      return this.statusOptions.map(o => ({ value: o.value, text: o.label }));
    },
    categorySelectOptions() {
      return Object.keys(claimSubcategories).map(key => ({ value: key, text: claimCategoryLabels[key] }));
    },
    subcategorySelectOptions() {
      const subs = claimSubcategories[this.editForm.category];
      if (!subs) return [{ value: null, text: '— none —' }];
      return [{ value: null, text: '— none —' }, ...subs.map(s => ({ value: s.value, text: s.label }))];
    },
    severitySelectOptions() {
      return claimSeverityOptions.map(o => ({ value: o.value, text: o.label }));
    },
    faultPartyOptions() {
      return claimFaultPartyOptions.map(o => ({ value: o.value, text: o.label }));
    },
    photosReceivedOptions() {
      return claimPhotosOptions.map(o => ({ value: o.value, text: o.label }));
    },
    booleanOptions() {
      return claimBooleanOptions.map(o => ({ value: o.value, text: o.label }));
    },
    summary() {
      const closed = c => c.status === 'closed_paid' || c.status === 'closed_denied';
      return {
        totalExposure: this.claims.reduce((sum, c) => sum + (parseFloat(c.amountPaid) || 0), 0),
        open: this.claims.filter(c => !closed(c)).length,
        closed: this.claims.filter(closed).length,
        recovered: this.claims.filter(closed).reduce((sum, c) => sum + (parseFloat(c.amountRecovered) || 0), 0),
      };
    },
  },
  mounted() {
    this._nowTimer = setInterval(() => { this.now = Date.now(); }, 30000);
  },
  beforeDestroy() {
    clearInterval(this._nowTimer);
  },
  async created() {
    this.loading = true;
    const leadId = this.$route.params.id;
    const [claimsRes, vehiclesRes] = await Promise.all([
      ApiClient.leads.claims.get({ leadId }),
      ApiClient.leads.vehicles.get({ leadId }),
    ]);
    const vehicleMap = Object.fromEntries((vehiclesRes.data.data || []).map(v => [v.id, v]));
    this.claims = (claimsRes.data.data || []).map(c => ({
      ...c,
      vehicle: vehicleMap[c.leadVehicleId] || null,
    }));
    this.loading = false;
    const newClaimId = this.$route.query.newClaimId ? Number(this.$route.query.newClaimId) : null;
    const openClaimId = this.$route.query.openClaimId ? Number(this.$route.query.openClaimId) : null;
    const targetClaimId = newClaimId || openClaimId;
    if (targetClaimId) {
      this.openClaimId = targetClaimId;
      this.highlightClaimId = targetClaimId;
      setTimeout(() => { this.highlightClaimId = null; }, 2200);
      this.$nextTick(() => {
        const el = document.querySelector(`[data-claim-id="${targetClaimId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    const [notesResults, attResults] = await Promise.all([
      Promise.all(this.claims.map(c => ApiClient.leads.claims.notes.get({ id: c.id }))),
      Promise.all(this.claims.map(c => ApiClient.leads.claims.attachments.get({ id: c.id }))),
    ]);
    notesResults.forEach((res, i) => {
      const id = this.claims[i].id;
      const notes = (res.data.data || []).slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      this.$set(this.claimNotes, id, notes);
    });
    attResults.forEach((res, i) => {
      this.$set(this.claimAttachments, this.claims[i].id, res.data.data || []);
    });
  },
  methods: {
    startEditClaim(claim, section) {
      this.editingClaimId = claim.id;
      this.editingSection = section;
      const { status, category, subcategory, severity, amountPaid, amountToRecover, repairEstimate, amountRecovered, faultParty, photosReceived, bolDocumented, slaDays, insuranceInvolved, description, emailThreadUrl } = claim;
      this.editForm = { status, category, subcategory: subcategory ?? null, severity, amountPaid, amountToRecover, repairEstimate, amountRecovered, faultParty: faultParty ?? null, photosReceived: photosReceived ?? null, bolDocumented: bolDocumented ?? null, slaDays, insuranceInvolved: insuranceInvolved ?? null, description: description || '', emailThreadUrl: emailThreadUrl || '' };
    },
    cancelEditClaim() {
      this.editingClaimId = null;
      this.editingSection = null;
      this.editForm = {};
    },
    async saveEditClaim(claimId) {
      await ApiClient.leads.claims.put({ id: claimId, body: this.editForm });
      const idx = this.claims.findIndex(c => c.id === claimId);
      if (idx !== -1) {
        this.$set(this.claims, idx, { ...this.claims[idx], ...this.editForm });
      }
      this.editingClaimId = null;
      this.editingSection = null;
      this.editForm = {};
    },
    isClaimOpen(claim) {
      return claim.status !== 'closed_paid' && claim.status !== 'closed_denied';
    },
    capitalize(val) {
      if (!val) return '';
      return val.charAt(0).toUpperCase() + val.slice(1);
    },
    _statusOpt(s) { return this.statusOptions.find(o => o.value === s) || {}; },
    statusLabel(s) { return this._statusOpt(s).label || s || ''; },
    statusBadgeClass(s) { return this._statusOpt(s).badge || ''; },
    statusTextClass(s) { return this._statusOpt(s).text || ''; },
    severityBadgeClass(severity) {
      return (claimSeverityOptions.find(o => o.value === severity) || {}).badge || '';
    },
    severityTextClass(severity) {
      return (claimSeverityOptions.find(o => o.value === severity) || {}).colorClass || '';
    },
    photosColorClass(val) {
      return (claimPhotosOptions.find(o => o.value === val) || {}).colorClass || '';
    },
    boolColorClass(val) {
      return (claimBooleanOptions.find(o => o.value === val) || {}).colorClass || '';
    },
    categoryLabel(category) {
      return claimCategoryLabels[category] || category;
    },
    subcategoryLabel(subcategory) {
      for (const subs of Object.values(claimSubcategories)) {
        const found = subs.find(s => s.value === subcategory);
        if (found) return found.label;
      }
      return subcategory;
    },
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    formatNoteDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    getSlaStatus(claim) {
      if (!claim.slaDueAt) return null;
      const d = differenceInDays(new Date(claim.slaDueAt), new Date());
      if (d > 3) return { color: 'green-text', text: `${d}d`, icon: '' };
      if (d >= 1) return { color: 'orange-text', text: `${d}d`, icon: 'fa-exclamation-triangle' };
      return { color: 'red-text', text: 'OD', icon: '' };
    },
    resolvedDays(claim) {
      if (!claim.openedAt || !claim.closedAt) return null;
      return Math.ceil((new Date(claim.closedAt) - new Date(claim.openedAt)) / 86400000);
    },
    toggleNoteForm(claimId) {
      this.$set(this.showNoteForm, claimId, !this.showNoteForm[claimId]);
    },
    async submitInlineNote(claimId, { status, note, escalationReason }) {
      this.$set(this.noteSaving, claimId, true);
      const calls = [];
      if (note && note.trim()) {
        calls.push(ApiClient.leads.claims.notes.post({ id: claimId, body: { note } }));
      }
      if (status) {
        const body = { status };
        if (status === 'escalated' && escalationReason) body.escalationReason = escalationReason;
        calls.push(ApiClient.leads.claims.put({ id: claimId, body }));
      }
      await Promise.all(calls);
      const claim = this.claims.find(c => c.id === claimId);
      if (claim && status) {
        claim.status = status;
        if (status === 'escalated') claim.escalationReason = escalationReason || null;
      }
      const notesRes = await ApiClient.leads.claims.notes.get({ id: claimId });
      const notes = (notesRes.data.data || []).slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      this.$set(this.claimNotes, claimId, notes);
      this.$set(this.noteSaving, claimId, false);
      this.$set(this.showNoteForm, claimId, false);
    },
    async downloadAttachment(claimId, att) {
      const res = await ApiClient.leads.claims.attachments.download({ id: claimId, att: att.id, blobRespone: true });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.name;
      a.click();
      URL.revokeObjectURL(url);
    },
    async deleteAttachment(claimId, att) {
      if (!confirm(`Delete "${att.name}"?`)) return;
      await ApiClient.leads.claims.attachments.delete({ id: claimId, att: att.id });
      this.$set(this.claimAttachments, claimId, this.claimAttachments[claimId].filter(a => a.id !== att.id));
    },
    async toggleAttPreview(claimId, att) {
      const key = `${claimId}-${att.id}`;
      if (this.expandedAtt === key) { this.expandedAtt = null; return; }
      this.expandedAtt = key;
      if (!this.attPreviewUrls[key]) {
        const res = await ApiClient.leads.claims.attachments.download({ id: claimId, att: att.id, blobRespone: true });
        const type = res.headers['content-type'] || 'application/octet-stream';
        this.$set(this.attPreviewUrls, key, URL.createObjectURL(new Blob([res.data], { type })));
      }
    },
    async saveClaimAttachment(claimId, { name, file }) {
      this.$set(this.attSaving, claimId, true);
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('file', file, file.name);
      await ApiClient.leads.claims.attachments.post({ id: claimId, body: fd });
      const attRes = await ApiClient.leads.claims.attachments.get({ id: claimId });
      this.$set(this.claimAttachments, claimId, attRes.data.data || []);
      this.$set(this.attSaving, claimId, false);
      this.$set(this.showAttForm, claimId, false);
      const refOrArr = this.$refs[`attForm-${claimId}`];
      const ref = Array.isArray(refOrArr) ? refOrArr[0] : refOrArr;
      if (ref) ref.reset();
    },
    isImage(name) { return /\.(png|jpe?g|gif|webp|svg|bmp|tiff?)$/i.test(name); },
    isPdf(name) { return /\.pdf$/i.test(name); },
    formatFileSize(x = 0) {
      if (x < 1000) return x + ' B';
      x = ('0'.repeat(((x += '').length * 2) % 3) + x).match(/.{3}/g);
      return Number(x[0]) + '.' + x[1].substring(0, 2) + ' ' + '  kMGTPEZY'[x.length] + 'B';
    },
    canEditNote(note) {
      if (this.isAdmin) return true;
      if (!note.createdAt) return false;
      return this.now - new Date(note.createdAt).getTime() < 10 * 60 * 1000;
    },
    startEditNote(claimId, note) {
      this.editingNote = { claimId, noteId: note.id, note: note.note };
    },
    cancelEditNote() {
      this.editingNote = null;
    },
    async saveEditNote(claimId) {
      const { noteId, note } = this.editingNote;
      if (!note.trim()) return;
      await ApiClient.leads.claims.notes.put({ id: claimId, note: noteId, body: { note: note.trim() } });
      const list = this.claimNotes[claimId].map(n => n.id === noteId ? { ...n, note: note.trim() } : n);
      this.$set(this.claimNotes, claimId, list);
      this.editingNote = null;
    },
    async deleteNote(claimId, note) {
      if (!confirm('Delete this note?')) return;
      await ApiClient.leads.claims.notes.delete({ id: claimId, note: note.id });
      this.$set(this.claimNotes, claimId, this.claimNotes[claimId].filter(n => n.id !== note.id));
    },
    async deleteClaim(claim) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Delete CLM-${String(claim.id).padStart(4, '0')}? This action cannot be undone.`,
        { title: 'Delete Claim', okVariant: 'danger', okTitle: 'Delete', centered: true }
      );
      if (!confirmed) return;
      const res = await ApiClient.claims.delete({ id: claim.id });
      if (res.status === 200 || res.status === 204) {
        this.claims = this.claims.filter(c => c.id !== claim.id);
      }
    },
    openCloseModal(claim) {
      this.closeModal.claimId = claim.id;
      this.closeModal.show = true;
    },
    async handleCloseSubmit({ status, resolutionType, amountRecovered, note }) {
      const { claimId } = this.closeModal;
      const calls = [
        ApiClient.leads.claims.put({
          id: claimId,
          body: { status, resolutionType, amountRecovered },
        }),
      ];
      if (note && note.trim()) {
        calls.push(ApiClient.leads.claims.notes.post({ id: claimId, body: { note } }));
      }
      await Promise.all(calls);
      const claim = this.claims.find(c => c.id === claimId);
      if (claim) {
        claim.status = status;
        claim.amountRecovered = amountRecovered;
      }
    },
  },
};
</script>
<style>
.redBadge { background-color: #F5DEE8; color: #C31813; border-color: #F1BABC; }
.dark-style .redBadge { background-color: #4F3748; border-color:#A34B48; color: #F86F6B;}
.orangeBadge { background-color: #F5E6E2; color: #9A3413; border-color: #F1CFBA; }
.dark-style .orangeBadge { background-color: #453D46; color: #FDBA74; border-color:#93602B; }
.yellowBadge { background-color: #FBF2BF; color: #885118; border-color: #E7D3B4;}
.dark-style .yellowBadge { background-color: #3D3618; color: #FFDF6D; border-color: #E7D3B4;}
.greenBadge { background-color: #DDECEA; color: #008E5A; border-color: #B7D6BF;}
.dark-style .greenBadge { background-color: #224332; color: #95D596; border-color: #308767;}
.grayBadge { background-color: #E1E3EF; color: #334155; border-color: #C9CBDB; }
.dark-style .grayBadge { background-color: #353F56; color: #CAD5E0; border-color:#758391; }
.blueBadge { background-color: #DFECFD; color: #0B8AFF; border-color: #B9CFEC; }
.dark-style .blueBadge { background-color: #2B446E; color: #44B1FF; border-color: #1A78BC; }
.purpleBadge { background-color: #EADFFD; color: #6B21A7; border-color: #D7CCEA; }
.dark-style .purpleBadge { background-color: #3D2763; color: #C084FC; border-color: #9A6CCD; }

.claimBadge {
  display: inline-block;
  padding: 2px 16px;
  border-radius: 100px;
  font-size: 0.78rem;
  font-weight: 500;
  border-width: 1px;
  border-style: solid;
}

.claim-field {
  padding: 5px 1.25rem;
  font-size: 0.875rem;
}
.claim-field-label { font-size: 0.75rem; color: #888; margin-bottom: 2px; }
.dark-style .claim-field-label { color: #aaa; }

.claim-att-row-clickable { cursor: pointer; }
.claim-att-row-clickable:hover { background: rgba(0,0,0,0.03); }
.dark-style .claim-att-row-clickable:hover { background: rgba(255,255,255,0.04); }

.claim-note-row {
  display: grid;
  grid-template-columns: 160px auto 1fr auto;
  gap: 12px;
  padding: 6px 12px;
  border-bottom: 1px solid rgba(0,0,0,0.04);
  align-items: start;
}
.claim-note-actions { display: flex; gap: 4px; flex-shrink: 0; }
.claim-note-row:last-child { border-bottom: none; }
.claim-note-date { white-space: nowrap; }
.claim-note-user { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.claim-note-text { word-break: break-word; }
.dark-style .claim-note-row { border-color: rgba(255,255,255,0.04); }


@keyframes claimFlash {
  0%   { box-shadow: none; }
  15%  { box-shadow: 0 0 0 3px #689be7, 0 0 14px rgba(35, 104, 207, 0.35); }
  75%  { box-shadow: 0 0 0 3px #689be7, 0 0 14px rgba(35, 104, 207, 0.35); }
  100% { box-shadow: none; }
}
.claim-highlight {
  animation: claimFlash 2.2s ease-in-out forwards;
  border-radius: 4px;
}

.dark-style .claim-accordion > .card-body { background-color: #26282D; }
</style>